import { type ReactNode, useCallback, useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'

import { AppErrorFallback } from './AppErrorFallback'
import { type AppErrorFallbackVariant } from './AppErrorFallback.styles'

import type { ILogger } from '@/infra/logger/ILogger'

interface AppErrorBoundaryProps {
  children: ReactNode
  logger: ILogger
  boundaryName?: string
  title?: string
  message?: string
  actionLabel?: string
  variant?: AppErrorFallbackVariant
  onAction?: () => void
  resetKey?: string
}

function AppErrorBoundary(props: AppErrorBoundaryProps) {
  const {
    children,
    logger,
    boundaryName = 'AppErrorBoundary',
    title = 'Ocorreu um erro inesperado', // TODO: i18n
    message = 'Recarregue o app para tentar novamente. Se o problema continuar, entre em contato com o nosso suporte.', // TODO: i18n
    actionLabel = 'Recarregar app', // TODO: i18n
    variant = 'fullscreen' as AppErrorFallbackVariant,
    onAction,
    resetKey
  } = props

  const [isDismissed, setIsDismissed] = useState(false)

  useEffect(() => {
    setIsDismissed(false)
  }, [resetKey])

  const handleAction = useCallback(() => {
    if (onAction) {
      onAction()
    } else {
      window.location.reload()
    }
  }, [onAction])

  const handleError = useCallback(
    (error: unknown, componentStack: string | undefined) => {
      if (!(error instanceof Error)) {
        logger.error(boundaryName, `Unknown error\n${componentStack?.trim() || 'No component stack available'}`)
        return
      }

      logger.error(
        boundaryName,
        `${error.name}: ${error.message}\n${componentStack?.trim() || 'No component stack available'}`
      )
    },
    [boundaryName, logger]
  )

  const handleBeforeCapture = useCallback(
    (scope: Sentry.Scope, _error: unknown, componentStack: string | undefined) => {
      scope.setTag('error_boundary', boundaryName)
      scope.setContext('react', {
        componentStack: componentStack?.trim() || 'No component stack available'
      })
    },
    [boundaryName]
  )

  if (isDismissed) {
    return null
  }

  return (
    <Sentry.ErrorBoundary
      key={resetKey ?? 'default'}
      beforeCapture={handleBeforeCapture}
      onError={handleError}
      fallback={({ resetError }) => (
        <AppErrorFallback
          onAction={handleAction}
          onClose={() => {
            resetError()
            setIsDismissed(true)
          }}
          title={title}
          message={message}
          actionLabel={actionLabel}
          variant={variant}
        />
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}

export { AppErrorBoundary }
